import React from "react";

/**
 * Status Badge Design System (shared canonical spec).
 * 6 semantic tones, SOLID background, WHITE bold text, subtle shadow, pill radius.
 * Only the background color changes with status.
 */

const TONE_COLORS = {
  completed: "#15803D",
  active: "#1D4ED8",
  pending: "#B45309",
  billing: "#6D28D9",
  cancelled: "#B91C1C",
  idle: "#475569",
};

const SIZES = {
  sm: { padding: "2px 8px", fontSize: "11px" },
  md: { padding: "3px 10px", fontSize: "12px" },
  lg: { padding: "4px 12px", fontSize: "14px" },
};

/**
 * Normalize any status string to one of the 6 tones.
 * PRIORITY ORDER (first match wins, case-insensitive).
 */
export const resolveStatusTone = (status) => {
  const s = (status == null ? "" : String(status)).trim();
  if (!s) return "idle";

  if (/(cancel|reject|declin|overdue|void|fail|expired|error|returned|offline)/i.test(s)) return "cancelled";
  if (/(pending|awaiting|await|waiting|on\s*hold|hold|to\s*do|todo|requested|unapproved|not\s*approved|queue)/i.test(s)) return "pending";
  if (/(billing|invoice|billed|finance|payment|receivable|payable)/i.test(s)) return "billing";
  if (/(completed|complete|approved|success|done|paid|delivered|collected|closed|finished|ready|settled)/i.test(s)) return "completed";
  if (/(progress|ongoing|processing|trucking|staging|truck|booking|booked|assigned|loading|transit|transfer|en\s*route|started|running|dispatch|moving|shipped|online|active)/i.test(s)) return "active";

  return "idle";
};

const StatusBadge = ({ status, label, tone, size = "md", dot = false, className }) => {
  const resolvedTone = tone && TONE_COLORS[tone] ? tone : resolveStatusTone(status);
  const bg = TONE_COLORS[resolvedTone] || TONE_COLORS.idle;
  const sizeStyle = SIZES[size] || SIZES.md;
  const text = label != null ? label : status;

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        whiteSpace: "nowrap",
        lineHeight: 1.35,
        letterSpacing: "0.02em",
        fontWeight: 700,
        color: "#FFFFFF",
        backgroundColor: bg,
        borderRadius: "999px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
        padding: sizeStyle.padding,
        fontSize: sizeStyle.fontSize,
      }}
    >
      {dot && (
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: "currentColor",
            opacity: 0.9,
            flexShrink: 0,
          }}
        />
      )}
      {text}
    </span>
  );
};

export default StatusBadge;
